import { utf8StringToBase64 } from '../crypto/base64.ts'
import { getAutoMediaDownload } from '../storage/privacySettingsStore.ts'
import { ONYX_APP_HEADER, isCapacitorNative, resolveSocket2Url, resolveSocket2WsUrl } from '../api/ZionServer.ts'
import {
  ensureSocketAuthKeyPair,
  getSocketAuthPublicKeyBase64,
  socketAuthSignRaw,
  socketAuthSigned,
} from '../crypto/socketAuth.ts'
import { dispatchHistoryPushEvent } from '../deviceLink/deviceLinkClient.ts'
import type { PairingRequest, ZionContact } from '../storage/pairingStore.ts'
import {
  findContactBySession,
  getPendingPairingRequest,
  setPendingPairingRequest,
  updateContactInboundQueue,
  upsertPairedContact,
} from '../storage/pairingStore.ts'
import { buildEphemeralTimerFields } from '../tempChat/ephemeralPayload.ts'
import { getSessionToken, saveSessionToken } from '../storage/sessionStore.ts'

export type BlobHandler = (queueId: string, blob: string, meta?: { peerSessionToken?: string }) => void
export type PairingHandler = (data: Record<string, unknown>) => void
export type MessageEchoHandler = (data: Record<string, unknown>) => void
export type TempChatHandler = (data: Record<string, unknown>) => void

let eventSource: EventSource | null = null
/** WebSocket nativo (Capacitor iOS) verso wss://socket2.zionyx.ch */
let nativeWs: WebSocket | null = null
let privateKey: CryptoKey | null = null
let sessionToken: string | null = null
let connected = false
let inboxPollTimer: number | null = null
let sseReconnectTimer: number | null = null
/** Mutex refresh trasparente — evita storm su 401 multipli. */
let sessionRefreshInFlight: Promise<string | null> | null = null
const watchedQueues = new Map<string, string>()
let onBlob: BlobHandler | null = null
let onPairRequest: PairingHandler | null = null
let onPairAccept: PairingHandler | null = null
let onPairReject: (() => void) | null = null
let onChatDeleted: PairingHandler | null = null
let onMessageEcho: MessageEchoHandler | null = null
let onTempChatCreate: TempChatHandler | null = null
let onTempChatMessage: TempChatHandler | null = null
let onTempChatExpired: TempChatHandler | null = null
let onTempChatAccept: TempChatHandler | null = null
let onTempChatReject: TempChatHandler | null = null

export class Socket2Error extends Error {
  code: string
  status: number

  constructor(message: string, code: string, status: number) {
    super(message)
    this.name = 'Socket2Error'
    this.code = code
    this.status = status
  }
}

function isAuthFailureStatus(status: number): boolean {
  return status === 401 || status === 403
}

function parseApiErrorCode(body: string): string {
  const text = body.trim()
  if (!text) return ''
  try {
    const data = JSON.parse(text) as { error?: string; code?: string; message?: string }
    return String(data.error ?? data.code ?? data.message ?? '').trim().toLowerCase()
  } catch {
    return text.toLowerCase()
  }
}

/** session_expired / not_paired / 401 / invalid_token → refresh + re-handshake silenzioso. */
export function isPairingOrAuthTransportError(status: number, errorCode?: string): boolean {
  const e = (errorCode ?? '').trim().toLowerCase()
  if (isAuthFailureStatus(status)) return true
  if (!e) return false
  return (
    e === 'session_expired' ||
    e === 'not_paired' ||
    e === 'invalid_token' ||
    e.includes('session_expired') ||
    e.includes('not_paired') ||
    e.includes('invalid_token')
  )
}

export type PushBlobResult = {
  ok: boolean
  authFailed: boolean
  pairingFailed: boolean
}

/** Payload JSON-safe per pair/temp — niente undefined/NaN che rompono decode server. */
export function sanitizePairPayload(payload: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) continue
    if (typeof value === 'number') {
      if (!Number.isFinite(value)) continue
      out[key] = value
      continue
    }
    if (typeof value === 'string' || typeof value === 'boolean') {
      out[key] = value
      continue
    }
    try {
      JSON.stringify(value)
      out[key] = value
    } catch {
      /* skip non-serializable */
    }
  }
  return out
}

function rewriteSessionTokensInBody(
  body: Record<string, unknown>,
  oldToken: string,
  newToken: string,
): Record<string, unknown> {
  if (!oldToken || !newToken || oldToken === newToken) return body
  const next: Record<string, unknown> = { ...body }
  for (const key of Object.keys(next)) {
    if (typeof next[key] === 'string' && next[key] === oldToken) {
      next[key] = newToken
    }
  }
  return next
}

/**
 * Soft renew sessione su 401/403 — non blocca UI, non wipe local store su fallimento.
 * Usato da push/sync/pair dopo errori auth.
 */
export async function refreshSessionTokenTransparent(): Promise<string | null> {
  if (sessionRefreshInFlight) return sessionRefreshInFlight
  sessionRefreshInFlight = (async () => {
    try {
      const prev =
        sessionToken?.trim() ||
        (await getSessionToken())?.trim() ||
        ''
      if (!prev) return null

      const pubKey = await getSocketAuthPublicKeyBase64()
      const createBody: Record<string, unknown> = {
        pubKey,
        acceptMedia: getAutoMediaDownload(),
        previousSessionToken: prev,
      }
      // Fetch diretto senza auto-refresh (evita loop)
      const res = await socketFetch('/session/create', {
        method: 'POST',
        body: JSON.stringify(createBody),
      })
      const text = await res.text()
      let data: { sessionToken?: string; error?: string } = {}
      try {
        data = text ? (JSON.parse(text) as typeof data) : {}
      } catch {
        console.warn('[ZION] session refresh: risposta non JSON')
        return null
      }
      if (!res.ok) {
        console.warn('[ZION] session refresh HTTP', res.status, data.error)
        return null
      }
      const next = String(data.sessionToken ?? '').trim()
      if (!next) return null

      await saveSessionToken(next)
      sessionToken = next
      // Riconnessione best-effort in background — UI non aspetta
      void connectSocket(next).catch((e) => {
        console.warn('[ZION] reconnect after refresh', e)
      })
      console.info('[ZION] session token refreshed transparently')
      return next
    } catch (e) {
      console.warn('[ZION] transparent session refresh failed', e)
      return null
    } finally {
      sessionRefreshInFlight = null
    }
  })()
  return sessionRefreshInFlight
}

async function socketFetch(path: string, init: RequestInit = {}): Promise<Response> {
  // Header minimi — niente Origin custom (WKWebView/CORS lo rifiuterebbe).
  // X-ONYX-App: su Capacitor passa via CapacitorHttp (bypass CORS).
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  }
  // Su native CapHttp non ha il limite Allow-Headers del browser
  if (isCapacitorNative() || !('Origin' in headers)) {
    headers['X-ONYX-App'] = ONYX_APP_HEADER
  }
  delete headers['Origin']
  delete headers['origin']

  const timeoutMs = isCapacitorNative() ? 20_000 : 12_000
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  const userSignal = init.signal
  if (userSignal) {
    if (userSignal.aborted) ctrl.abort()
    else userSignal.addEventListener('abort', () => ctrl.abort(), { once: true })
  }
  try {
    const url = resolveSocket2Url(path)
    // Capacitor: CapacitorHttp bypassa CORS; no credentials / no Origin custom
    return await fetch(url, {
      ...init,
      headers,
      signal: ctrl.signal,
      mode: 'cors',
      credentials: 'omit',
      cache: 'no-store',
      referrerPolicy: 'no-referrer',
    })
  } catch (err) {
    const aborted =
      (err instanceof DOMException && err.name === 'AbortError') ||
      (err instanceof Error && /abort|timeout/i.test(err.message))
    const corsLike =
      err instanceof TypeError ||
      (err instanceof Error && /cors|failed to fetch|load failed|networkerror/i.test(err.message))
    // Su Capacitor: non bloccare UI con "Rete non disponibile" — soft error + retry WS in background
    if (isCapacitorNative() && !aborted) {
      console.warn('[ZION] fetch soft-fail (CORS/rete) — WSS retry in background', err)
      throw new Socket2Error(
        'Connessione in corso…',
        corsLike ? 'network_soft' : 'network_unreachable',
        0,
      )
    }
    throw new Socket2Error(
      aborted
        ? 'Timeout rete. Controlla la connessione.'
        : 'Rete/socket non raggiungibile (LOAD FAILED).',
      aborted ? 'session_timeout' : 'network_unreachable',
      0,
    )
  } finally {
    clearTimeout(timer)
  }
}

async function postJson(path: string, body: Record<string, unknown>): Promise<unknown | null> {
  const attempt = async (payload: Record<string, unknown>): Promise<{ data: unknown | null; authFailed: boolean }> => {
    try {
      const res = await socketFetch(path, { method: 'POST', body: JSON.stringify(payload) })
      const text = await res.text()
      if (isAuthFailureStatus(res.status) && path !== '/session/create') {
        return { data: null, authFailed: true }
      }
      if (!res.ok) return { data: null, authFailed: false }
      try {
        return { data: text ? JSON.parse(text) : {}, authFailed: false }
      } catch {
        return { data: null, authFailed: false }
      }
    } catch {
      return { data: null, authFailed: false }
    }
  }

  const first = await attempt(body)
  if (!first.authFailed) return first.data

  const oldTok = String(body.sessionToken ?? sessionToken ?? '').trim()
  const refreshed = await refreshSessionTokenTransparent()
  if (!refreshed) return null
  // Body firmati (ts+sig) vanno ri-firmati dal caller — non ritentare con sig stale
  if (typeof body.sig === 'string' || typeof body.ts === 'string') {
    return null
  }
  const retryBody = rewriteSessionTokensInBody(body, oldTok || refreshed, refreshed)
  if (typeof retryBody.sessionToken === 'string') retryBody.sessionToken = refreshed
  const second = await attempt(retryBody)
  return second.data
}

async function postJsonOrThrow(path: string, body: Record<string, unknown>): Promise<unknown> {
  const attempt = async (payload: Record<string, unknown>): Promise<{ data?: unknown; error?: Socket2Error; authFailed?: boolean }> => {
    const res = await socketFetch(path, { method: 'POST', body: JSON.stringify(payload) })
    const text = await res.text()
    let data: Record<string, unknown> = {}
    try {
      data = text ? (JSON.parse(text) as Record<string, unknown>) : {}
    } catch {
      /* ignore */
    }
    if (!res.ok) {
      const code = String(data.error ?? 'request_failed')
      if (isAuthFailureStatus(res.status) && path !== '/session/create' && code !== 'cap_reached') {
        return { authFailed: true }
      }
      if (res.status === 403 && code === 'cap_reached') {
        const max = Number(data.max ?? 1000)
        return {
          error: new Socket2Error(
            `Beta piena: raggiunto il limite di ${max} utenti. Riprova più tardi.`,
            'cap_reached',
            403,
          ),
        }
      }
      return {
        error: new Socket2Error(String(data.message ?? data.error ?? 'request_failed'), code, res.status),
      }
    }
    return { data }
  }

  const first = await attempt(body)
  if (first.error) throw first.error
  if (!first.authFailed) return first.data

  const oldTok = String(body.sessionToken ?? body.previousSessionToken ?? sessionToken ?? '').trim()
  const refreshed = await refreshSessionTokenTransparent()
  if (!refreshed) {
    throw new Socket2Error('Sessione scaduta. Riprova.', 'unauthorized', 401)
  }
  const retryBody = rewriteSessionTokensInBody(body, oldTok || refreshed, refreshed)
  if ('sessionToken' in retryBody) retryBody.sessionToken = refreshed
  const second = await attempt(retryBody)
  if (second.error) throw second.error
  if (second.authFailed) {
    throw new Socket2Error('Sessione scaduta. Riprova.', 'unauthorized', 401)
  }
  return second.data
}

export function setBlobHandler(handler: BlobHandler | null): void {
  onBlob = handler
}

export function setMessageEchoHandler(handler: MessageEchoHandler | null): void {
  onMessageEcho = handler
}

export function setPairingHandlers(handlers: {
  onRequest?: PairingHandler | null
  onAccept?: PairingHandler | null
  onReject?: (() => void) | null
  onChatDeleted?: PairingHandler | null
}): void {
  onPairRequest = handlers.onRequest ?? null
  onPairAccept = handlers.onAccept ?? null
  onPairReject = handlers.onReject ?? null
  onChatDeleted = handlers.onChatDeleted ?? null
}

export function setTempChatHandlers(handlers: {
  onCreate?: TempChatHandler | null
  onMessage?: TempChatHandler | null
  onExpired?: TempChatHandler | null
  onAccept?: TempChatHandler | null
  onReject?: TempChatHandler | null
}): void {
  onTempChatCreate = handlers.onCreate ?? null
  onTempChatMessage = handlers.onMessage ?? null
  onTempChatExpired = handlers.onExpired ?? null
  onTempChatAccept = handlers.onAccept ?? null
  onTempChatReject = handlers.onReject ?? null
}

export function isSocketConnected(): boolean {
  const esOk = !!eventSource && eventSource.readyState !== EventSource.CLOSED
  const wsOk = !!nativeWs && nativeWs.readyState === WebSocket.OPEN
  return connected && !!sessionToken && (esOk || wsOk)
}

/** Riconnette SSE/WSS se spento. Safe da chiamare prima di ogni push. */
export async function ensureSocketReady(): Promise<boolean> {
  const tok = sessionToken?.trim()
  if (!tok) return false
  const esOk = eventSource && eventSource.readyState === EventSource.OPEN
  const wsOk = nativeWs && nativeWs.readyState === WebSocket.OPEN
  if (connected && (esOk || wsOk)) return true
  try {
    await connectSocket(tok)
    return isSocketConnected() || connected
  } catch (e) {
    console.warn('[ZION] ensureSocketReady failed', e)
    return false
  }
}

export function watchQueue(queueId: string, ownerToken: string): void {
  watchedQueues.set(queueId, ownerToken)
}

export function unwatchQueue(queueId: string): void {
  watchedQueues.delete(queueId)
}

export async function createSession(previousToken?: string | null): Promise<string> {
  const pubKey = await getSocketAuthPublicKeyBase64()
  const body: Record<string, unknown> = { pubKey, acceptMedia: getAutoMediaDownload() }
  if (previousToken?.trim()) body.previousSessionToken = previousToken.trim()

  const createPromise = postJsonOrThrow('/session/create', body) as Promise<{ sessionToken?: string }>
  void createPromise.catch(() => undefined)

  const raceMs = isCapacitorNative() ? 15_000 : 5_000
  const data = await Promise.race([
    createPromise,
    new Promise<never>((_, reject) => {
      window.setTimeout(
        () =>
          reject(
            new Socket2Error(
              `Timeout rete (${Math.round(raceMs / 1000)}s). Controlla la connessione.`,
              'session_timeout',
              408,
            ),
          ),
        raceMs,
      )
    }),
  ])
  const token = data?.sessionToken?.trim()
  if (!token) throw new Socket2Error('Creazione sessione fallita', 'session_create_failed', 500)
  return token
}

export async function createMessageQueue(sessionTok: string): Promise<string> {
  const pubKey = await getSocketAuthPublicKeyBase64()
  const data = (await postJson('/queue/create', {
    sessionToken: sessionTok,
    pubKey,
  })) as { queueId?: string } | null
  const queueId = data?.queueId?.trim()
  if (!queueId) throw new Error('queue_create_failed')
  return queueId
}

export async function registerDailyCode(sessionTok: string, nickname?: string): Promise<string> {
  const body: Record<string, string> = { sessionToken: sessionTok }
  const nick = nickname?.trim()
  if (nick) body.nickname = nick
  const data = (await postJson('/discovery/register', body)) as { dailyCode?: string } | null
  const code = data?.dailyCode?.trim().toUpperCase()
  if (!code) throw new Error('discovery_register_failed')
  return code
}

export async function resolveDailyCode(code: string): Promise<{ sessionToken: string; pubKey: string } | null> {
  const q = encodeURIComponent(code.trim().toUpperCase())
  const res = await socketFetch(`/discovery/resolve?code=${q}`, { method: 'GET' })
  if (!res.ok) return null
  const text = await res.text()
  try {
    const data = JSON.parse(text) as { sessionToken?: string; pubKey?: string; error?: string }
    if (data.error) return null
    const sessionToken = data.sessionToken?.trim()
    const pubKey = data.pubKey?.trim()
    if (!sessionToken || !pubKey) return null
    return { sessionToken, pubKey }
  } catch {
    return null
  }
}

/** Endpoint /session/media-prefs non esiste più sul server (404). No-op silenzioso. */
export async function syncMediaPrefs(
  _sessionToken: string,
  _acceptMedia: boolean,
): Promise<boolean> {
  return true
}

export async function pushBlobWithResult(
  queueId: string,
  blob: string,
  toSessionToken?: string,
  contentKind = 'text',
  meta?: {
    senderSessionToken?: string
    messageId?: string
    toNickname?: string
    fromNickname?: string
  },
): Promise<PushBlobResult> {
  const qid = queueId.trim()
  const bodyBlob = blob.trim()
  if (!qid || !bodyBlob) return { ok: false, authFailed: false, pairingFailed: false }

  const body: Record<string, string> = { queueId: qid, blob: bodyBlob, contentKind }
  const peer = toSessionToken?.trim()
  if (peer) body.toSessionToken = peer
  const sender = meta?.senderSessionToken?.trim()
  if (sender) body.senderSessionToken = sender
  const msgId = meta?.messageId?.trim()
  if (msgId) body.messageId = msgId
  const toNick = meta?.toNickname?.trim()
  if (toNick) body.toNickname = toNick
  const fromNick = meta?.fromNickname?.trim()
  if (fromNick) body.fromNickname = fromNick

  const attempt = async (): Promise<PushBlobResult> => {
    try {
      await ensureSocketReady()
      const res = await socketFetch('/queue/push', {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (res.ok) return { ok: true, authFailed: false, pairingFailed: false }
      const errText = await res.text()
      const errCode = parseApiErrorCode(errText)
      const transportFail = isPairingOrAuthTransportError(res.status, errCode)
      return {
        ok: false,
        authFailed: isAuthFailureStatus(res.status) || errCode.includes('invalid_token'),
        pairingFailed: transportFail,
      }
    } catch (e) {
      console.warn('[ZION pushBlob] network error', e)
      return { ok: false, authFailed: false, pairingFailed: false }
    }
  }

  const first = await attempt()
  if (first.ok) return first
  if (first.authFailed || first.pairingFailed) {
    const refreshed = await refreshSessionTokenTransparent()
    if (refreshed) {
      if (body.senderSessionToken) body.senderSessionToken = refreshed
      const afterAuth = await attempt()
      if (afterAuth.ok) return afterAuth
    }
  }
  try {
    const tok = sessionToken?.trim()
    if (tok) await connectSocket(tok)
  } catch {
    /* ignore */
  }
  return await attempt()
}

export async function pushBlob(
  queueId: string,
  blob: string,
  toSessionToken?: string,
  contentKind = 'text',
  meta?: {
    senderSessionToken?: string
    messageId?: string
    toNickname?: string
    fromNickname?: string
  },
): Promise<boolean> {
  return (
    await pushBlobWithResult(queueId, blob, toSessionToken, contentKind, meta)
  ).ok
}

/** Bridge E2E blob → socket.io private_message (Android). Richiede patch server ZION_TEMP_CHAT_CROSSPLATFORM_V1. */
export async function relayPrivateBlob(opts: {
  toSessionToken: string
  fromSessionToken: string
  blob: string
  messageId: string
  toNickname?: string
  fromNickname?: string
}): Promise<{ ok: boolean; delivered: boolean }> {
  const toSessionToken = opts.toSessionToken.trim()
  const fromSessionToken = opts.fromSessionToken.trim()
  const blob = opts.blob.trim()
  if (!toSessionToken || !fromSessionToken || !blob) return { ok: false, delivered: false }
  try {
    const res = await socketFetch('/relay/private-blob', {
      method: 'POST',
      body: JSON.stringify({
        toSessionToken,
        fromSessionToken,
        blob,
        messageId: opts.messageId.trim(),
        toNickname: opts.toNickname?.trim() ?? '',
        fromNickname: opts.fromNickname?.trim() ?? '',
      }),
    })
    if (!res.ok) return { ok: false, delivered: false }
    try {
      const data = (await res.json()) as { ok?: boolean; delivered?: boolean }
      return { ok: true, delivered: data.delivered === true }
    } catch {
      return { ok: true, delivered: false }
    }
  } catch {
    return { ok: false, delivered: false }
  }
}

export type QueuePullResult = {
  blobs: string[]
  queueMissing: boolean
  canonicalQueueId?: string
}

export async function pullBlobs(queueId: string): Promise<QueuePullResult> {
  const qid = queueId.trim()
  if (!qid) return { blobs: [], queueMissing: false }
  try {
    if (!privateKey) {
      const pair = await ensureSocketAuthKeyPair()
      privateKey = pair.privateKey
    }
    const { ts, sig } = await socketAuthSigned(privateKey, qid)
    const res = await socketFetch('/queue/pull', {
      method: 'POST',
      body: JSON.stringify({ queueId: qid, ts, sig }),
    })
    // 401/404: endpoint assente o non autorizzato — non spam/throw
    if (res.status === 401 || res.status === 403 || res.status === 404) {
      return { blobs: [], queueMissing: res.status === 404 }
    }
    if (!res.ok) return { blobs: [], queueMissing: false }
    const data = (await res.json()) as { blobs?: unknown; queueId?: string }
    const blobs = Array.isArray(data?.blobs)
      ? data.blobs.filter((b): b is string => typeof b === 'string' && b.trim().length > 0)
      : []
    return {
      blobs,
      queueMissing: false,
      canonicalQueueId: typeof data?.queueId === 'string' ? data.queueId.trim() || undefined : undefined,
    }
  } catch {
    return { blobs: [], queueMissing: false }
  }
}

/** Come Android pullSessionInbox — inbox sessione + tutte le code del proprietario. */
export async function pullSessionBlobs(sessionToken: string): Promise<QueuePullResult> {
  const session = sessionToken.trim()
  if (!session) return { blobs: [], queueMissing: false }
  try {
    if (!privateKey) {
      const pair = await ensureSocketAuthKeyPair()
      privateKey = pair.privateKey
    }
    const { ts, sig } = await socketAuthSigned(privateKey, session)
    const res = await socketFetch('/queue/pull', {
      method: 'POST',
      body: JSON.stringify({ pullMode: 'session', sessionToken: session, ts, sig }),
    })
    if (res.status === 401 || res.status === 403 || res.status === 404) {
      return { blobs: [], queueMissing: res.status === 404 }
    }
    if (!res.ok) return { blobs: [], queueMissing: false }
    const data = (await res.json()) as { blobs?: unknown; queueId?: string }
    const blobs = Array.isArray(data?.blobs)
      ? data.blobs.filter((b): b is string => typeof b === 'string' && b.trim().length > 0)
      : []
    return {
      blobs,
      queueMissing: false,
      canonicalQueueId: typeof data?.queueId === 'string' ? data.queueId.trim() || undefined : undefined,
    }
  } catch {
    return { blobs: [], queueMissing: false }
  }
}

export async function broadcastQueueUpdate(
  mySessionToken: string,
  myQueueId: string,
  peerSessionTokens: string[],
): Promise<void> {
  for (const peer of peerSessionTokens) {
    const token = peer.trim()
    if (!token) continue
    await sendPairEvent(token, mySessionToken, 'queue_update', {
      myQueueId,
      mySessionToken,
    })
  }
}

export type PairSendResult = { ok: true } | { ok: false; error?: string }

async function sendPairEvent(
  toSessionToken: string,
  fromSessionToken: string,
  type: string,
  payload: Record<string, unknown>,
): Promise<PairSendResult> {
  if (!privateKey) {
    const pair = await ensureSocketAuthKeyPair()
    privateKey = pair.privateKey
  }

  const safePayload = sanitizePairPayload(payload)
  let payloadB64: string
  try {
    payloadB64 = utf8StringToBase64(JSON.stringify(safePayload))
  } catch (e) {
    console.warn('[ZION] pair payload encode failed', type, e)
    return { ok: false, error: 'payload_encode_failed' }
  }

  const postOnce = async (fromTok: string): Promise<{ ok: boolean; status: number; error?: string }> => {
    const ts = String(Date.now())
    const signPayload = `${fromTok}:${toSessionToken}:${type}:${ts}`
    const sig = await socketAuthSignRaw(privateKey!, signPayload)
    try {
      const res = await socketFetch('/pair/send', {
        method: 'POST',
        body: JSON.stringify({
          toSessionToken,
          fromSessionToken: fromTok,
          type,
          payload: payloadB64,
          ts,
          sig,
        }),
      })
      if (res.ok) return { ok: true, status: res.status }
      const text = await res.text()
      let err = 'request_failed'
      try {
        const data = JSON.parse(text) as { error?: string }
        err = data.error ?? err
      } catch {
        /* non-JSON body */
      }
      return { ok: false, status: res.status, error: err }
    } catch (e) {
      console.warn('[ZION] sendPairEvent socket error', type, e)
      return { ok: false, status: 0, error: 'socket_error' }
    }
  }

  let fromTok = fromSessionToken.trim() || sessionToken?.trim() || ''
  const first = await postOnce(fromTok)
  if (first.ok) return { ok: true }

  const err = (first.error ?? '').toLowerCase()
  if (isPairingOrAuthTransportError(first.status, err) || err === 'socket_error') {
    const refreshed = await refreshSessionTokenTransparent()
    if (refreshed) {
      fromTok = refreshed
      const second = await postOnce(fromTok)
      if (second.ok) return { ok: true }
      return { ok: false, error: second.error ?? first.error }
    }
  }
  return { ok: false, error: first.error }
}

export async function sendPairRequest(opts: {
  toSessionToken: string
  mySessionToken: string
  myPublicKey: string
  myNickname: string
  myQueueId: string
}): Promise<PairSendResult> {
  return sendPairEvent(opts.toSessionToken, opts.mySessionToken, 'pair_request', {
    myPublicKey: opts.myPublicKey,
    myNickname: opts.myNickname,
    myQueueId: opts.myQueueId,
    mySessionToken: opts.mySessionToken,
  })
}

export async function sendPairAccept(opts: {
  toSessionToken: string
  mySessionToken: string
  myPublicKey: string
  myNickname: string
  myQueueId: string
}): Promise<boolean> {
  const result = await sendPairEvent(opts.toSessionToken, opts.mySessionToken, 'pair_accept', {
    myPublicKey: opts.myPublicKey,
    myNickname: opts.myNickname,
    mySessionToken: opts.mySessionToken,
    myQueueId: opts.myQueueId,
  })
  return result.ok
}

export async function sendPairReject(toSessionToken: string, fromSessionToken: string): Promise<boolean> {
  const result = await sendPairEvent(toSessionToken, fromSessionToken, 'pair_reject', {})
  return result.ok
}

/** Come Android ZionSocket.deleteChat — evento pairing chat_deleted. */
export async function sendChatDeleted(
  toSessionToken: string,
  fromSessionToken: string,
): Promise<boolean> {
  const result = await sendPairEvent(toSessionToken, fromSessionToken, 'chat_deleted', {})
  return result.ok
}

export async function sendTempChatCreate(opts: {
  toSessionToken: string
  mySessionToken: string
  chatKey: string
  expiresAt: number
  durationMs: number
  myNickname: string
  inviteCode?: string
  initiatorSessionToken?: string
  targetSessionToken?: string
  peerNickname?: string
}): Promise<boolean> {
  const lane = opts.chatKey.trim().toLowerCase()
  const peerNick = opts.peerNickname?.trim() ?? ''
  const ephemeral = buildEphemeralTimerFields(opts.durationMs)
  const expiresAt = Math.round(Number(opts.expiresAt) || Date.now() + ephemeral.durationMs)
  const result = await sendPairEvent(opts.toSessionToken, opts.mySessionToken, 'temp_chat_create', {
    chatKey: lane,
    lane,
    key: lane,
    expiresAt,
    inviteCode: opts.inviteCode?.trim() || undefined,
    ...ephemeral,
    mySessionToken: opts.mySessionToken,
    myNickname: String(opts.myNickname || '').trim(),
    nickname: String(opts.myNickname || '').trim(),
    initiatorSessionToken: opts.initiatorSessionToken ?? opts.mySessionToken,
    targetSessionToken: opts.targetSessionToken ?? opts.toSessionToken,
    toSessionToken: opts.toSessionToken,
    targetNickname: peerNick,
    peerNickname: peerNick,
  })
  return result.ok
}

export async function sendTempChatAccept(opts: {
  toSessionToken: string
  mySessionToken: string
  chatKey: string
  myNickname: string
  initiatorSessionToken?: string
  acceptorSessionToken?: string
  durationMs?: number
  expiresAt?: number
}): Promise<boolean> {
  const lane = opts.chatKey.trim().toLowerCase()
  const durationMs =
    opts.durationMs ??
    (opts.expiresAt != null ? Math.max(60_000, opts.expiresAt - Date.now()) : 24 * 60 * 60 * 1000)
  const ephemeral = buildEphemeralTimerFields(durationMs)
  const expiresAt = Math.round(Number(opts.expiresAt) || Date.now() + ephemeral.durationMs)
  const result = await sendPairEvent(opts.toSessionToken, opts.mySessionToken, 'temp_chat_accept', {
    chatKey: lane,
    lane,
    key: lane,
    mySessionToken: opts.mySessionToken,
    myNickname: String(opts.myNickname || '').trim(),
    nickname: String(opts.myNickname || '').trim(),
    initiatorSessionToken: opts.initiatorSessionToken ?? opts.toSessionToken,
    acceptorSessionToken: opts.acceptorSessionToken ?? opts.mySessionToken,
    toSessionToken: opts.toSessionToken,
    expiresAt,
    ...ephemeral,
  })
  return result.ok
}

export async function sendTempChatReject(opts: {
  toSessionToken: string
  mySessionToken: string
  chatKey: string
  initiatorSessionToken?: string
  rejectorSessionToken?: string
}): Promise<boolean> {
  const lane = opts.chatKey.trim().toLowerCase()
  console.warn('[ZION-TEMP] PAIR temp_chat_reject inviato', {
    chatKey: lane,
    to: opts.toSessionToken.slice(0, 8),
    from: opts.mySessionToken.slice(0, 8),
    stack: new Error().stack?.split('\n').slice(1, 5).join(' | '),
  })
  const result = await sendPairEvent(opts.toSessionToken, opts.mySessionToken, 'temp_chat_reject', {
    chatKey: lane,
    lane,
    mySessionToken: opts.mySessionToken,
    initiatorSessionToken: opts.initiatorSessionToken ?? opts.toSessionToken,
    rejectorSessionToken: opts.rejectorSessionToken ?? opts.mySessionToken,
    toSessionToken: opts.toSessionToken,
  })
  return result.ok
}

export async function sendTempChatMessage(opts: {
  toSessionToken: string
  mySessionToken: string
  chatKey: string
  message: string
  messageId: string
  durationMs?: number
}): Promise<boolean> {
  const lane = opts.chatKey.trim().toLowerCase()
  const ephemeral = buildEphemeralTimerFields(opts.durationMs ?? 24 * 60 * 60 * 1000)
  const result = await sendPairEvent(opts.toSessionToken, opts.mySessionToken, 'temp_chat_message', {
    chatKey: lane,
    lane,
    key: lane,
    message: String(opts.message ?? ''),
    messageId: String(opts.messageId ?? '').trim(),
    mySessionToken: opts.mySessionToken,
    toSessionToken: opts.toSessionToken,
    ...ephemeral,
  })
  return result.ok
}

export async function sendTempChatExpired(opts: {
  toSessionToken: string
  mySessionToken: string
  chatKey: string
}): Promise<boolean> {
  const lane = opts.chatKey.trim().toLowerCase()
  const result = await sendPairEvent(opts.toSessionToken, opts.mySessionToken, 'temp_chat_expired', {
    chatKey: lane,
    lane,
    key: lane,
    mySessionToken: opts.mySessionToken,
    toSessionToken: opts.toSessionToken,
  })
  return result.ok
}

function tempEventFromSession(data: Record<string, unknown>, normalized: Record<string, unknown>): string {
  return String(
    data.fromSessionToken ??
      normalized.fromSessionToken ??
      normalized.acceptorSessionToken ??
      normalized.initiatorSessionToken ??
      normalized.creatorSessionToken ??
      normalized.mySessionToken ??
      '',
  ).trim()
}

function tempEventToSession(data: Record<string, unknown>, normalized: Record<string, unknown>): string {
  return String(
    data.toSessionToken ??
      normalized.toSessionToken ??
      normalized.targetSessionToken ??
      '',
  ).trim()
}

function enrichTempPairPayload(
  normalized: Record<string, unknown>,
  data: Record<string, unknown>,
): Record<string, unknown> {
  const chatKey = String(normalized.chatKey ?? normalized.lane ?? normalized.key ?? '')
    .trim()
    .toLowerCase()
  return {
    ...normalized,
    chatKey: chatKey || normalized.chatKey,
    lane: chatKey || normalized.lane,
    fromSessionToken: tempEventFromSession(data, normalized),
    toSessionToken: tempEventToSession(data, normalized),
  }
}
function decodePairPayload(data: Record<string, unknown>): Record<string, unknown> {
  const merged = { ...data }
  const payloadB64 = String(data.payload ?? '').trim()
  if (payloadB64) {
    try {
      const inner = JSON.parse(atob(payloadB64)) as Record<string, unknown>
      Object.assign(merged, inner)
    } catch {
      /* ignore */
    }
  }
  return merged
}

async function postDeliveryAck(sessionTok: string, ackId: string): Promise<void> {
  await postJson('/session/delivery-ack', { sessionToken: sessionTok, ackId })
}

async function handlePairEvent(data: Record<string, unknown>): Promise<void> {
  const type = String(data.type ?? '').toLowerCase()
  const normalized = decodePairPayload(data)
  if (type === 'pair_request') {
    const req: PairingRequest = {
      fromSessionToken: String(
        normalized.fromSessionToken ?? normalized.mySessionToken ?? data.fromSessionToken ?? '',
      ).trim(),
      publicKey: String(normalized.myPublicKey ?? normalized.publicKey ?? '').trim(),
      nickname: String(normalized.myNickname ?? normalized.nickname ?? '').trim(),
      queueId: String(normalized.myQueueId ?? '').trim() || undefined,
    }
    if (req.fromSessionToken && req.publicKey) {
      const existing = await findContactBySession(req.fromSessionToken)
      if (existing && req.queueId && req.queueId !== existing.inboundQueueId) {
        await upsertPairedContact(
          {
            sessionToken: req.fromSessionToken,
            publicKey: req.publicKey,
            nickname: req.nickname || existing.nickname,
            inboundQueueId: req.queueId,
          },
          { refreshPairedAt: true },
        )
      }
      await setPendingPairingRequest(req)
      onPairRequest?.(normalized)
    }
    return
  }
  if (type === 'pair_accept') {
    onPairAccept?.(normalized)
    return
  }
  if (type === 'queue_update') {
    const fromSession = String(
      normalized.mySessionToken ?? normalized.fromSessionToken ?? data.fromSessionToken ?? '',
    ).trim()
    const newQueue = String(normalized.myQueueId ?? '').trim()
    if (fromSession && newQueue) {
      void updateContactInboundQueue(fromSession, newQueue).then((contact) => {
        if (contact) watchQueue(newQueue, fromSession)
      })
    }
    return
  }
  if (type === 'pair_reject') {
    onPairReject?.()
    return
  }
  if (type === 'chat_deleted') {
    onChatDeleted?.(normalized)
    return
  }
  if (type === 'temp_chat_create') {
    onTempChatCreate?.(enrichTempPairPayload(normalized, data))
    return
  }
  if (type === 'temp_chat_message') {
    onTempChatMessage?.(enrichTempPairPayload(normalized, data))
    return
  }
  if (type === 'temp_chat_expired') {
    onTempChatExpired?.(enrichTempPairPayload(normalized, data))
    return
  }
  if (type === 'temp_chat_accept') {
    onTempChatAccept?.(enrichTempPairPayload(normalized, data))
    return
  }
  if (type === 'temp_chat_reject') {
    onTempChatReject?.(enrichTempPairPayload(normalized, data))
  }
}

async function pullQueueBlobs(queueId: string): Promise<void> {
  try {
    const qid = queueId.trim()
    if (!qid) return
    const { blobs } = await pullBlobs(qid)
    const list = Array.isArray(blobs) ? blobs : []
    for (const blob of list) {
      if (blob?.trim()) onBlob?.(qid, blob)
    }
  } catch (e) {
    console.error('[ZION] pullQueueBlobs suppressed:', e)
  }
}

async function pullOwnSessionBlobs(): Promise<void> {
  try {
    const session = sessionToken?.trim()
    if (!session) return
    const { blobs } = await pullSessionBlobs(session)
    const list = Array.isArray(blobs) ? blobs : []
    for (const blob of list) {
      if (blob?.trim()) onBlob?.('', blob)
    }
  } catch (e) {
    console.error('[ZION] pullOwnSessionBlobs suppressed:', e)
  }
}

async function dispatchEvent(data: Record<string, unknown>): Promise<void> {
  const ackId = String(data.ackId ?? '').trim()
  const tok = sessionToken?.trim()
  if (ackId && tok) void postDeliveryAck(tok, ackId)

  if (dispatchHistoryPushEvent(data)) return

  const type = String(data.type ?? '').toLowerCase()
  if (type === 'new_inbox') {
    const tok = sessionToken?.trim()
    if (tok) await syncInbox(tok)
    return
  }
  if (type === 'message_echo') {
    onMessageEcho?.(data)
    return
  }
  if (type.startsWith('pair_') || type === 'chat_deleted' || type.startsWith('temp_chat_')) {
    await handlePairEvent(data)
    return
  }
  if (type === 'new_blob' || type === 'receive_message') {
    const inlineBlob = String(data.blob ?? data.message ?? '').trim()
    const peerSession = String(
      data.peerSessionToken ?? data.senderSessionToken ?? data.fromSessionToken ?? '',
    ).trim()
    if (inlineBlob) {
      const queueId = String(data.queueId ?? '').trim()
      onBlob?.(queueId, inlineBlob, peerSession ? { peerSessionToken: peerSession } : undefined)
      const tok = sessionToken?.trim()
      if (tok) {
        try {
          await syncInbox(tok)
        } catch (e) {
          console.error('[ZION] syncInbox suppressed:', e)
        }
      }
      return
    }
    // Solo pull one-shot della propria sessione + coda indicata dall'evento (NO watchedQueues spam)
    try {
      await pullOwnSessionBlobs()
      const queueId = String(data.queueId ?? '').trim()
      if (queueId) await pullQueueBlobs(queueId)
      const tok = sessionToken?.trim()
      if (tok) await syncInbox(tok)
    } catch (e) {
      console.error('[ZION] new_blob pull suppressed:', e)
    }
    return
  }
  if (type === 'wake') {
    try {
      await pullOwnSessionBlobs()
      const tok = sessionToken?.trim()
      if (tok) await syncInbox(tok)
    } catch (e) {
      console.error('[ZION] wake pull suppressed:', e)
    }
    return
  }
  const blob = String(data.blob ?? data.message ?? '').trim()
  if (blob) {
    const queueId = String(data.queueId ?? '').trim()
    onBlob?.(queueId, blob)
  }
}

/** Scarica blob della propria sessione (event-driven). NON itera le code peer watched. */
async function pullSessionUpdates(): Promise<void> {
  try {
    await pullOwnSessionBlobs()
    const tok = sessionToken?.trim()
    if (tok) await syncInbox(tok)
  } catch (e) {
    console.error('[ZION] pullSessionUpdates suppressed:', e)
  }
}

export async function syncInbox(sessionTok: string): Promise<void> {
  try {
    let tok = sessionTok.trim() || sessionToken?.trim() || ''
    if (!tok) return
    if (!privateKey) {
      const pair = await ensureSocketAuthKeyPair()
      privateKey = pair.privateKey
    }
    const run = async (session: string) => {
      const { ts, sig } = await socketAuthSigned(privateKey!, session)
      return postJson('/session/inbox', { sessionToken: session, ts, sig })
    }
    let data = (await run(tok)) as { events?: unknown[] } | null
    // postJson su 401 fa refresh e restituisce null → ri-firma con token nuovo
    const refreshed = sessionToken?.trim()
    if (data == null && refreshed && refreshed !== tok) {
      tok = refreshed
      data = (await run(tok)) as { events?: unknown[] } | null
    }
    const events = Array.isArray(data?.events) ? data!.events! : []
    for (const ev of events) {
      if (ev && typeof ev === 'object') {
        await dispatchEvent(ev as Record<string, unknown>)
      } else if (typeof ev === 'string') {
        try {
          await dispatchEvent(JSON.parse(ev) as Record<string, unknown>)
        } catch {
          /* ignore */
        }
      }
    }
  } catch (e) {
    console.error('[ZION] syncInbox suppressed:', e)
  }
}

export async function pollInboxBurst(
  sessionTok: string,
  times = 6,
  intervalMs = 2000,
): Promise<void> {
  for (let i = 0; i < times; i++) {
    try {
      await pullSessionUpdates()
      await syncInbox(sessionTok)
    } catch (e) {
      console.error('[ZION] Sync error suppressed:', e)
    }
    if (i < times - 1) {
      await new Promise((r) => window.setTimeout(r, intervalMs))
    }
  }
}

export function disconnectSocket(): void {
  eventSource?.close()
  eventSource = null
  wssLoopActive = false
  if (nativeWs) {
    try {
      nativeWs.onclose = null
      nativeWs.onerror = null
      nativeWs.onmessage = null
      nativeWs.close()
    } catch {
      /* ignore */
    }
    nativeWs = null
  }
  if (inboxPollTimer != null) {
    window.clearInterval(inboxPollTimer)
    inboxPollTimer = null
  }
  if (sseReconnectTimer != null) {
    window.clearTimeout(sseReconnectTimer)
    sseReconnectTimer = null
  }
  if (wssReconnectTimer != null) {
    window.clearTimeout(wssReconnectTimer)
    wssReconnectTimer = null
  }
  connected = false
  sessionToken = null
}

const SSE_RECONNECT_MS = 2000
const WSS_RECONNECT_MS = 2000
let wssReconnectTimer: number | null = null
let wssLoopActive = false

/** Riconnessione aggressiva ogni 2s finché SSE/WS non è aperto (iOS Cap). */
function scheduleSseReconnect(): void {
  if (sseReconnectTimer != null || !sessionToken || !connected) return
  sseReconnectTimer = window.setTimeout(() => {
    sseReconnectTimer = null
    const tok = sessionToken?.trim()
    if (!tok || !connected) return
    const esOpen = eventSource && eventSource.readyState === EventSource.OPEN
    const wsOpen = nativeWs && nativeWs.readyState === WebSocket.OPEN
    if (esOpen || wsOpen) return
    void startRealtimeChannel(tok)
  }, SSE_RECONNECT_MS)
}

/** Loop WSS Capacitor: retry ogni 2s senza errori UI. */
function scheduleWssReconnect(): void {
  if (!wssLoopActive || !isCapacitorNative()) return
  if (wssReconnectTimer != null || !sessionToken || !connected) return
  if (nativeWs && nativeWs.readyState === WebSocket.OPEN) return
  wssReconnectTimer = window.setTimeout(() => {
    wssReconnectTimer = null
    const tok = sessionToken?.trim()
    if (!tok || !connected || !wssLoopActive) return
    if (nativeWs && nativeWs.readyState === WebSocket.OPEN) return
    void tryNativeWebSocket(tok)
  }, WSS_RECONNECT_MS)
}

function handleRealtimePayload(raw: string): void {
  const text = String(raw ?? '').trim()
  if (!text) return
  try {
    void dispatchEvent(JSON.parse(text) as Record<string, unknown>).catch((e) => {
      console.error('[ZION] realtime dispatch suppressed:', e)
    })
  } catch (e) {
    console.error('[ZION] realtime parse suppressed:', e)
  }
}

/**
 * Canale realtime verso socket2.
 * Capacitor iOS: WSS primario wss://socket2.zionyx.ch (nessun Origin custom).
 * CORS/WebKit warning → retry background, UI non bloccata.
 */
async function startRealtimeChannel(tok: string): Promise<void> {
  if (!privateKey) return
  try {
    if (isCapacitorNative()) {
      wssLoopActive = true
      void tryNativeWebSocket(tok)
      // SSE best-effort (può fallire CORS da capacitor://) — non blocca
      void startEventSource(tok).catch((err) => {
        console.warn('[ZION] SSE soft-fail Cap — WSS/poll restano attivi', err)
      })
      return
    }
    await startEventSource(tok)
  } catch (err) {
    console.warn('[ZION] realtime start soft-fail', err)
    scheduleSseReconnect()
    if (isCapacitorNative()) scheduleWssReconnect()
  }
}

/**
 * WSS diretto su Capacitor: solo `new WebSocket(wssUrl)` — niente protocolli/Origin.
 * Handshake CORS warning → riconnessione background silenziosa.
 */
async function tryNativeWebSocket(tok: string): Promise<void> {
  if (!privateKey || !isCapacitorNative()) return
  try {
    const { ts, sig } = await socketAuthSigned(privateKey, tok)
    const listenPath = `/session/listen?token=${encodeURIComponent(tok)}&ts=${encodeURIComponent(ts)}&sig=${encodeURIComponent(sig)}`
    // Forza SEMPRE wss://socket2.zionyx.ch
    let wssUrl = resolveSocket2WsUrl(listenPath)
    if (!wssUrl.startsWith('wss://socket2.zionyx.ch')) {
      const q = listenPath.includes('?') ? listenPath.slice(listenPath.indexOf('?')) : ''
      const pathOnly = listenPath.split('?')[0] || '/session/listen'
      wssUrl = `wss://socket2.zionyx.ch${pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`}${q}`
    }

    if (nativeWs) {
      try {
        nativeWs.onclose = null
        nativeWs.onerror = null
        nativeWs.onmessage = null
        nativeWs.close()
      } catch {
        /* ignore */
      }
      nativeWs = null
    }

    // Un solo argomento: niente protocols / niente Origin restrittivo (WebKit iOS)
    const ws = new WebSocket(wssUrl)
    nativeWs = ws

    ws.onopen = () => {
      console.info('[ZION] WSS connected', 'wss://socket2.zionyx.ch/session/listen')
      if (eventSource) {
        try {
          eventSource.close()
        } catch {
          /* ignore */
        }
        eventSource = null
      }
      if (sseReconnectTimer != null) {
        window.clearTimeout(sseReconnectTimer)
        sseReconnectTimer = null
      }
      if (wssReconnectTimer != null) {
        window.clearTimeout(wssReconnectTimer)
        wssReconnectTimer = null
      }
    }

    ws.onmessage = (ev) => {
      const data = typeof ev.data === 'string' ? ev.data : ''
      for (const line of data.split('\n')) {
        const trimmed = line.trim()
        if (!trimmed || trimmed.startsWith(':')) continue
        if (trimmed.startsWith('data:')) handleRealtimePayload(trimmed.slice(5).trim())
        else if (trimmed.startsWith('{')) handleRealtimePayload(trimmed)
      }
    }

    ws.onerror = () => {
      // Warning CORS / handshake WebKit: non propagare a UI
      console.warn('[ZION] WSS handshake warn — retry background (no UI block)')
      try {
        ws.close()
      } catch {
        /* ignore */
      }
      if (nativeWs === ws) nativeWs = null
      scheduleWssReconnect()
    }

    ws.onclose = () => {
      if (nativeWs === ws) nativeWs = null
      if (connected && sessionToken && wssLoopActive) scheduleWssReconnect()
    }
  } catch (err) {
    console.warn('[ZION] WSS init soft-fail — retry background', err)
    scheduleWssReconnect()
  }
}

async function startEventSource(tok: string): Promise<void> {
  if (!privateKey) return
  try {
    const { ts, sig } = await socketAuthSigned(privateKey, tok)
    const url = resolveSocket2Url(
      `/session/listen?token=${encodeURIComponent(tok)}&ts=${encodeURIComponent(ts)}&sig=${encodeURIComponent(sig)}`,
    )
    // Chiudi event source precedente
    if (eventSource) {
      try {
        eventSource.close()
      } catch {
        /* ignore */
      }
      eventSource = null
    }
    const es = new EventSource(url)
    eventSource = es
    es.onopen = () => {
      console.info('[ZION] SSE connected', url.replace(/\?.*/, ''))
      if (sseReconnectTimer != null) {
        window.clearTimeout(sseReconnectTimer)
        sseReconnectTimer = null
      }
    }
    es.onmessage = (ev) => {
      handleRealtimePayload(String(ev.data ?? ''))
    }
    es.onerror = () => {
      console.warn('[ZION] SSE offline — reconnect 2s', resolveSocket2Url('/session/listen'))
      try {
        es.close()
      } catch {
        /* ignore */
      }
      if (eventSource === es) eventSource = null
      scheduleSseReconnect()
    }
  } catch {
    scheduleSseReconnect()
  }
}

export async function connectSocket(sessionTok: string): Promise<void> {
  disconnectSocket()
  sessionToken = sessionTok.trim()
  const pair = await ensureSocketAuthKeyPair()
  privateKey = pair.privateKey
  connected = true
  await syncInbox(sessionToken)
  await startRealtimeChannel(sessionToken)

  const pollMs = isCapacitorNative() ? 2000 : 4000
  inboxPollTimer = window.setInterval(() => {
    try {
      if (sessionToken && connected) void syncInbox(sessionToken)
    } catch (e) {
      console.error('[ZION] inbox poll suppressed:', e)
    }
  }, pollMs)
}
export async function completePairingFromAccept(
  data: Record<string, unknown>,
  myNickname: string,
): Promise<ZionContact> {
  const sessionToken = String(data.mySessionToken ?? data.fromSessionToken ?? '').trim()
  const publicKey = String(data.myPublicKey ?? data.publicKey ?? '').trim()
  const nickname = String(data.myNickname ?? data.nickname ?? '').trim()
  const inboundQueueId = String(data.myQueueId ?? data.queueId ?? '').trim()
  const contact = await upsertPairedContact({
    sessionToken,
    publicKey,
    nickname: nickname || myNickname,
    inboundQueueId,
  }, { refreshPairedAt: true })
  if (inboundQueueId) watchQueue(inboundQueueId, sessionToken)
  await setPendingPairingRequest(null)
  return contact
}

export async function acceptPendingPairing(opts: {
  request: PairingRequest
  mySessionToken: string
  myPublicKey: string
  myNickname: string
  myQueueId: string
}): Promise<ZionContact | null> {
  const ok = await sendPairAccept({
    toSessionToken: opts.request.fromSessionToken,
    mySessionToken: opts.mySessionToken,
    myPublicKey: opts.myPublicKey,
    myNickname: opts.myNickname,
    myQueueId: opts.myQueueId,
  })
  if (!ok) return null
  const contact = await upsertPairedContact({
    sessionToken: opts.request.fromSessionToken,
    publicKey: opts.request.publicKey,
    nickname: opts.request.nickname,
    inboundQueueId: opts.request.queueId ?? '',
  }, { refreshPairedAt: true })
  if (contact.inboundQueueId) watchQueue(contact.inboundQueueId, contact.sessionToken)
  await broadcastQueueUpdate(opts.mySessionToken, opts.myQueueId, [opts.request.fromSessionToken])
  await setPendingPairingRequest(null)
  return contact
}

export { setHistoryPushHandler } from '../deviceLink/deviceLinkClient.ts'

export async function getPendingRequest(): Promise<PairingRequest | null> {
  return getPendingPairingRequest()
}
